import React from 'react';
import { Sparkles } from 'lucide-react';
import { useLedgerFacts } from '@/hooks/useLedgerFacts';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * How many facts the rail shows. The ledger is unbounded by design (it
 * compacts by token budget, not by count), and a consult can accumulate
 * dozens. The rail is glanceable context beside a live conversation, not a
 * reading surface — past about six lines the doctor stops scanning it, and
 * anything genuinely important is in the note at the end anyway.
 */
const MAX_SHOWN = 6;

/**
 * "What MAIE has picked up so far" — the rolling clinical fact ledger,
 * rendered beside the live transcript during capture.
 *
 * Deliberately passive: nothing here is actionable, nothing asks the
 * clinician to confirm anything, and it never steals focus. The doctor is
 * talking to a patient; a surface that demands attention mid-consult is
 * worse than no surface. Everything shown lands in the note at Stop
 * regardless of whether it was read.
 *
 * Renders NOTHING until facts exist. `EXTENDED_RECORDING_LEDGER` is off by
 * default and the ledger has never been validated against a live model, so
 * "no facts" is the normal case, not an error worth an empty state.
 */
export const LedgerPickupRail: React.FC<{ sessionId: string | null }> = ({
  sessionId,
}) => {
  const { isRTL } = useTranslation();
  const { facts } = useLedgerFacts(sessionId);

  if (facts.length === 0) return null;

  // Highest-salience first: when the list is truncated, what survives
  // should be what the model thought mattered, not what happened to be
  // said first. Ties keep their existing order.
  const shown = [...facts]
    .sort((a, b) => (b.salience ?? 0) - (a.salience ?? 0))
    .slice(0, MAX_SHOWN);
  const hidden = facts.length - shown.length;

  return (
    <div className="flex-shrink-0 px-5 pb-2 sm:pb-3">
      <div
        className="rounded-xl px-3 py-2.5"
        style={{
          backgroundColor:
            'color-mix(in srgb, var(--color-primary-600) 6%, transparent)',
          border:
            '1px solid color-mix(in srgb, var(--color-primary-600) 22%, transparent)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: 'var(--color-primary-600)' }}
          />
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {isRTL ? 'ما تم رصده' : 'Picked up so far'}
          </span>
        </div>

        <ul className="flex flex-col gap-1.5">
          {shown.map((fact) => (
            <li key={fact.id} className="flex items-start gap-2">
              <span
                aria-hidden
                className="mt-[7px] w-1 h-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'var(--color-primary-600)' }}
              />
              <span
                className="text-xs leading-relaxed min-w-0"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {fact.text}
              </span>
            </li>
          ))}
        </ul>

        {hidden > 0 ? (
          <div
            className="mt-2 text-[10px]"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {isRTL ? `و${hidden} أخرى` : `and ${hidden} more`}
          </div>
        ) : null}

        <div
          className="mt-2 text-[10px]"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          {isRTL
            ? 'كل هذا سيدخل الملاحظة عند الإيقاف.'
            : 'All of this goes into the note when you stop.'}
        </div>
      </div>
    </div>
  );
};

export default LedgerPickupRail;
